/*
 * Farida Gupta - "N/total" product count, tracking the furthest grid row currently in view
 * (theme/sections/main-search.liquid, theme/sections/craft-main-collection.liquid, and
 * theme/sections/main-collection.liquid).
 *
 * The shared load-more script (fg-load-more.js) jumps [data-collection-count] straight to the new
 * total as soon as a page of products is fetched (e.g. 46 -> 92 on click). The client wants the
 * visible count to track scroll position instead: it climbs as a new row scrolls into view and
 * drops back down when the shopper scrolls back up past a row - so it takes over the count text
 * here and recomputes it purely from which rows are currently in view, overriding whatever
 * fg-load-more.js writes into the same span when it appends a new page of cards.
 *
 * Row membership is read from each card's offsetTop rather than a fixed "N per row" assumption,
 * since the grid's items-per-row is responsive across breakpoints and, on the craft collection,
 * promo banner tiles can shift product-card rows around.
 */
class SearchRowCount extends HTMLElement {
  connectedCallback() {
    this.countElement = this.querySelector('[data-collection-count]');
    this.list = document.getElementById(this.getAttribute('list-id'));

    if (!this.countElement || !this.list) return;

    this.cards = [];
    this.rowStartIndexes = [];
    this.rowStartByElement = new Map();
    this.visibleRowStarts = new Set();

    this.rowObserver = new IntersectionObserver(this.onRowIntersect.bind(this), {
      rootMargin: '0px 0px -10% 0px',
    });

    // Also rebuild when cards are un-hidden by fg-reveal-more.js (collection "Availability" sort).
    // Only the list's own children matter - ignore hidden toggles deeper inside a card.
    this.cardsObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' || mutation.target.parentElement === this.list)) {
        this.rebuild();
      }
    });
    this.cardsObserver.observe(this.list, { childList: true, subtree: true, attributeFilter: ['hidden'] });

    this.onResize = this.debounce(() => this.rebuild(), 200);
    window.addEventListener('resize', this.onResize);

    this.rebuild();
  }

  disconnectedCallback() {
    this.rowObserver?.disconnect();
    this.cardsObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  debounce(callback, delay) {
    let timeoutId;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  }

  // Re-reads the grid (new cards from load-more/backfill, or a resize that changes how many cards
  // fit per row) and restarts row observation from scratch.
  rebuild() {
    this.rowObserver.disconnect();

    // Hidden (not yet revealed) cards have no layout position, so they're left out of the rows.
    this.cards = [...this.list.children].filter((element) => element.tagName === 'PRODUCT-CARD' && !element.hidden);
    this.rowStartIndexes = [];
    this.rowStartByElement = new Map();
    this.visibleRowStarts = new Set();

    let lastTop = null;

    this.cards.forEach((card, index) => {
      const top = card.offsetTop;

      if (top !== lastTop) {
        this.rowStartIndexes.push(index);
        this.rowStartByElement.set(card, index);
        this.rowObserver.observe(card);
        lastTop = top;
      }
    });

    this.render();
  }

  // Keeps a live set of which rows are currently on screen - entries add a row when it scrolls into
  // view and remove it when it scrolls back out (either edge, so scrolling up drops rows too), then
  // the count is derived from the furthest-down row still in that set.
  onRowIntersect(entries) {
    entries.forEach((entry) => {
      const rowStart = this.rowStartByElement.get(entry.target);

      if (rowStart === undefined) return;

      if (entry.isIntersecting) {
        this.visibleRowStarts.add(rowStart);
      } else {
        this.visibleRowStarts.delete(rowStart);
      }
    });

    this.render();
  }

  render() {
    if (this.visibleRowStarts.size === 0) return;

    const maxVisibleRowStart = Math.max(...this.visibleRowStarts);
    const nextRowStart = this.rowStartIndexes.find((start) => start > maxVisibleRowStart);
    const countThroughRow = nextRowStart ?? this.cards.length;

    this.countElement.textContent = String(Math.min(countThroughRow, this.cards.length));
  }
}

if (!window.customElements.get('search-row-count')) {
  window.customElements.define('search-row-count', SearchRowCount);
}
